import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  ATTRIBUTION_ROLES,
  DEFAULT_ATTRIBUTION_ORDER,
  MAX_PERCENT_MILLI,
  REMAKE_CAUSES,
} from '@lensmira/domain'

import type { MessageKey } from '@lensmira/i18n'

import { DataSurface } from './DataSurface.js'
import { Ltr, useTranslation } from '../i18n.js'
import {
  MoneyInputError,
  formatMinorForInput,
  formatWireForDisplay,
  parseMajorInput,
  parseMinorUnits,
} from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import {
  attributionRoleLabel,
  commissionBaseLabel,
  commissionBasisLabel,
  commissionDeductionLabel,
  commissionEarnsOnLabel,
  commissionRoundingLabel,
  commissionStatusWord,
  commissionTierMeasureLabel,
  commissionTierScopeLabel,
  remakeCauseLabel,
} from '../status-labels.js'

/**
 * How staff are paid. §6.24 `COM-001` to `COM-004` and `COM-007`, flow §15.15 steps before step 1.
 *
 * **The configuration half only.** `GET /commission/schemes`, `POST /commission/schemes`,
 * `POST /commission/schemes/:id/versions` and
 * `POST /commission/schemes/:id/versions/:n/publication` are the four routes here. Evaluating a
 * period and approving it are deliberately absent: `COM-005`'s five behaviour metrics have no
 * source anywhere in the product, so `POST /commission/periods` can be called only by a manager
 * typing every reading for every person from figures they worked out elsewhere, and the statement
 * that came out would be only as true as that typing. `later_todo.md` 22 records it as a product
 * call rather than a screen, and a period screen built on that would ask the shop to do the
 * arithmetic the product exists to do.
 *
 * **A scheme is an identity and its versions, as a promotion and a price book are.** A published
 * version is frozen from the moment it is published, by trigger `9015`, because an entry computed
 * under it has to stay explainable (`COM-008`: disputes about commission destroy trust in the whole
 * system). So nothing here edits a published version: changing the rules writes a new draft
 * version, and putting that draft in force withdraws the old one in the same transaction with
 * "Superseded by version N", which the server does and this screen does not duplicate.
 *
 * **Nothing has a default, and the screen says why for each one.** The spec names no rounding mode
 * (Appendix D names none either), no trigger for when a line starts to earn, no basis, no discount
 * treatment for a retail rate and no remake treatment. Every one of them changes somebody's pay, so
 * each is a blank choice that refuses rather than a value this screen picked. `COM-003`'s
 * resolution order is the one exception with a documented default, and it is offered as a button
 * that fills the order in rather than as a pre-filled form: `DEFAULT_ATTRIBUTION_ORDER` exists "so
 * nobody retypes it" and is still the tenant's decision to take.
 *
 * **Paying on margin is not offered, and that is a gap rather than a decision.** `COM-002` makes a
 * margin basis inherit the cost-price permission, and `validateScheme` refuses one from a caller
 * without it, at create and again at publish. The permission is `cost_price_view` **and** the
 * per-branch grant `staff_branch_access.cost_price_visible`, which `BR-12` defaults to off and
 * nothing in this product sets. `GET /auth/context` sends the role-level permission list and not
 * the grant, so this screen cannot tell whether the server would accept a margin rule, and a
 * refused write stops the whole queue for everybody at the till. So the margin basis and the margin
 * tier measure are left out with a sentence saying so, and a listed scheme that already pays on
 * margin offers neither Change nor Put in force. One prop lifts this as soon as the context carries
 * the grant.
 *
 * **A rule's line kind is free text because the product's line category is free text.**
 * `commission-source.ts` reads `scheme.rules[sales_order_line.category]`, `NewOrder` asks for that
 * category as a typed field, and the column has no check constraint, so there is no vocabulary to
 * offer. A kind that matches nothing earns nothing and nothing reports it, which the hint beside
 * the field says in as many words.
 *
 * **A revision carries `metricRules` through untouched.** `COM-005`'s bonuses are out of this
 * slice, and a revision replaces the whole definition, so a screen that simply left them out would
 * erase a behaviour bonus the shop is paying. Same defect, and the same fix, as `keepCondition` in
 * `Promotions`. Anything else in the stored definition that this build has no words for refuses the
 * revision instead of being dropped.
 */

/** One line kind's rule. `COM-002`'s four bases; the basis stays a string per `ARC-020`. */
export interface SchemeRuleData {
  readonly lineKind: string
  readonly basis: string
  /** `flat`. Minor units of the scheme's currency. */
  readonly amountMinor?: string
  /** `retail` and `margin`. Thousandths of a percent, so 2.5 per cent is "2500". */
  readonly rateMilliPercent?: string
  /** `retail`: `list` pays before a discount, `net` on what was charged. No default. */
  readonly base?: string
  /** `margin`: what a line sold below cost earns. Never rendered, because margin is not offered. */
  readonly negativeMargin?: string
  /** `lens_tier`. The tier names are whatever the tenant calls them. */
  readonly amountByTier?: readonly { readonly tier: string; readonly amountMinor: string }[]
}

export interface SchemeTierStepData {
  readonly thresholdMinor: string
  /** Whether the threshold itself counts. The spec says "above" and nothing more. */
  readonly inclusive: boolean
  readonly rateMilliPercent: string
}

/** `COM-004`'s threshold rule. Absent means the scheme has no higher rate. */
export interface SchemeTierData {
  readonly scope: string
  readonly measure: string
  /** Only `whole_period` exists in the domain. Unnarrowed, so a later mode arrives as itself. */
  readonly mode: string
  readonly steps: readonly SchemeTierStepData[]
}

/** `COM-007`. Which remake causes reduce pay, and by how much. */
export interface SchemeRemakeData {
  readonly reducingCauses: readonly string[]
  readonly deductionKind: string
  readonly deductionAmountMinor?: string
}

export interface CommissionSchemeVersionData {
  /** The version's own id: what a period names when it is evaluated under it. */
  readonly id: string
  readonly schemeId: string
  readonly versionNumber: number
  /** The row version, for `ARC-007`'s optimistic lock on the publication. */
  readonly version: number
  readonly branchId: string
  /** Fixed on the scheme, so a version cannot change it. Every amount below is in it. */
  readonly currency: string
  /** `draft`, `published` or `withdrawn`. Unnarrowed per `ARC-020`. */
  readonly status: string
  readonly label: string
  readonly rounding: string
  readonly earnsOn: string
  readonly attributionOrder: readonly string[]
  readonly rules: readonly SchemeRuleData[]
  readonly tier?: SchemeTierData
  /**
   * `COM-005`'s bonuses, element by element exactly as stored, never reshaped.
   *
   * A revision posts them back unchanged. The count is all this screen reads, because the editor
   * for them is out of scope while `later_todo.md` 22 is open.
   */
  readonly metricRules?: readonly unknown[]
  readonly remake: SchemeRemakeData
  readonly publishedAt?: string
  readonly withdrawnAt?: string
  readonly withdrawReason?: string
}

export interface CommissionSchemesProps {
  readonly load: () => Promise<readonly CommissionSchemeVersionData[]>
  /** `BR-01`: the branch a new scheme is raised at, and where its currency comes from. */
  readonly branchId: string
  /**
   * The branches this person can reach, to name the one a scheme belongs to.
   *
   * The list spans every branch they can see, so a row from elsewhere has to say where it is from,
   * and it was saying it with a UUID.
   */
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  /** This branch's own currency, for a new scheme. A listed version uses its own. */
  readonly currency: string
}

/**
 * The only tier mode the domain defines, sent as a constant rather than chosen.
 *
 * `TierRule.mode` has one member: a marginal mode, where the higher rate applies only to the part
 * above the threshold, is a different calculation and the spec does not say which is meant. A
 * select with one option would be a question with one answer, and a default this screen invented
 * would be worse. `commission.tierMeans` states what `whole_period` does instead.
 */
const TIER_MODE = 'whole_period'

/** `COM-002`'s bases this screen can write. `margin` is absent for the reason in the header. */
const OFFERED_BASES = ['flat', 'retail', 'lens_tier'] as const

/** The rounding modes and earning triggers, as the codec's `oneOf` spells them. */
const ROUNDINGS = ['half_up', 'down'] as const
const EARNS_ON = ['sale', 'full_payment'] as const
const RETAIL_BASES = ['list', 'net'] as const
const TIER_SCOPES = ['person', 'branch'] as const
/** `margin` is absent here too: `usesMargin` counts a margin tier measure as a margin scheme. */
const OFFERED_TIER_MEASURES = ['retail'] as const
const DEDUCTION_KINDS = ['none', 'fixed', 'line_commission'] as const

/**
 * Everything this build has words for, so a revision can refuse rather than quietly drop a value.
 *
 * `ARC-020` keeps an unknown value readable on the list. A revision is the opposite problem: it
 * replaces the whole definition, so a basis or a mode this screen cannot show would be gone from
 * the next version. Checked against what can be written, not against what can be read: `margin` is
 * a basis this screen understands and still cannot write.
 */
const WRITABLE = {
  basis: OFFERED_BASES as readonly string[],
  rounding: ROUNDINGS as readonly string[],
  earnsOn: EARNS_ON as readonly string[],
  base: RETAIL_BASES as readonly string[],
  tierScope: TIER_SCOPES as readonly string[],
  tierMeasure: OFFERED_TIER_MEASURES as readonly string[],
  deduction: DEDUCTION_KINDS as readonly string[],
  role: ATTRIBUTION_ROLES as readonly string[],
  cause: REMAKE_CAUSES as readonly string[],
}

/** Thrown with a sentence already in the shop's language. Caught once, in `save`. */
class SchemeFormError extends Error {}

/**
 * A percentage a shopkeeper typed, to the thousandths of a percent the wire carries.
 *
 * Appendix H holds a rate as `numeric(6,3)` and `packages/domain/src/commission.ts` carries it as
 * an integer number of thousandths: 2.5 per cent is `2500`, 100 per cent is `100000`. So the field
 * asks for a percentage and this is the only place that converts one.
 *
 * Parsed against a pattern and assembled with integer arithmetic, never `Number()` or
 * `parseFloat`, for `MR-01`'s reason applied to a rate: `0.1 * 1000` is `100.00000000000001` in
 * floating point, and `bigFromWire` refuses anything that is not a string of whole digits with a
 * 422 that stops the queue. It is not in `money.ts` because a rate is not an amount of money and
 * that module's currency decimals have nothing to say about it; if a second screen needs it, it
 * moves then.
 *
 * The whole part is `\d+` and not `\d{1,3}` on purpose, so the **bound is the domain's
 * `MAX_PERCENT_MILLI`** and not a digit count written here. Three digits in the pattern would cap
 * the value at 999.999 by themselves, which reads as the same rule and is not one: the limit is
 * `numeric(6,3)`'s, Appendix H owns it, and a check that can never fire is the shape of guard this
 * repository keeps finding in its own tests. The fraction stays at three digits because that is the
 * scale rather than the range.
 */
export function parseRatePercent(input: string): bigint {
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(input.trim())
  if (!match) throw new Error('rate')
  const whole = BigInt(match[1] as string)
  const thousandths = BigInt((match[2] ?? '').padEnd(3, '0'))
  const milli = whole * 1000n + thousandths
  if (milli > MAX_PERCENT_MILLI) throw new Error('rate')
  return milli
}

/**
 * The inverse, for a rate field somebody is about to edit.
 *
 * Trailing zeroes are dropped so a rate stored as `2500` reads back as `2.5` rather than `2.500`,
 * which is what was typed. `parseRatePercent` accepts either, so the round trip is exact.
 *
 * Anything that is not a string of whole digits comes back as itself rather than throwing, in the
 * spirit of `ARC-020`: the server writes these through `bigFromWire` so one cannot be stored, and a
 * list that unmounted the whole screen over a single malformed rate would hide every other scheme
 * as well.
 */
export function formatRatePercent(milliPercent: string): string {
  if (!/^\d+$/.test(milliPercent)) return milliPercent
  const milli = BigInt(milliPercent)
  const whole = (milli / 1000n).toString()
  const fraction = (milli % 1000n).toString().padStart(3, '0').replace(/0+$/, '')
  return fraction === '' ? whole : `${whole}.${fraction}`
}

interface LensTierRow {
  readonly tier: string
  readonly amount: string
}

interface RuleRow {
  readonly lineKind: string
  readonly basis: string
  readonly amount: string
  readonly rate: string
  readonly base: string
  readonly lensTiers: readonly LensTierRow[]
}

interface StepRow {
  readonly threshold: string
  /** Empty until somebody chooses. "Above" is ambiguous in the spec, so it is asked. */
  readonly inclusive: string
  readonly rate: string
}

interface Form {
  readonly label: string
  readonly rounding: string
  readonly earnsOn: string
  readonly attributionOrder: readonly string[]
  readonly rules: readonly RuleRow[]
  readonly tierOn: boolean
  readonly tierScope: string
  readonly tierMeasure: string
  readonly steps: readonly StepRow[]
  readonly reducingCauses: readonly string[]
  readonly deductionKind: string
  readonly deductionAmount: string
  /** `COM-005`, carried from the version being revised and posted back untouched. */
  readonly keepMetricRules?: readonly unknown[]
}

const EMPTY_RULE: RuleRow = {
  lineKind: '',
  basis: '',
  amount: '',
  rate: '',
  base: '',
  lensTiers: [],
}
const EMPTY_STEP: StepRow = { threshold: '', inclusive: '', rate: '' }

const EMPTY_FORM: Form = {
  label: '',
  rounding: '',
  earnsOn: '',
  attributionOrder: [],
  rules: [],
  tierOn: false,
  tierScope: '',
  tierMeasure: '',
  steps: [],
  reducingCauses: [],
  deductionKind: '',
  deductionAmount: '',
}

/** Which scheme a new version is being written for, and the currency its amounts are in. */
interface Revising {
  readonly schemeId: string
  readonly currency: string
}

export function CommissionSchemes({
  load,
  branchId,
  branches,
  currency,
}: CommissionSchemesProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly CommissionSchemeVersionData[] | null>(null)
  const [form, setForm] = useState<Form>(EMPTY_FORM)
  const [revising, setRevising] = useState<Revising | null>(null)
  const [nextRole, setNextRole] = useState('')
  // Form errors only. A failed read has its own variable, or a bad rate typed into the form would
  // blank the list and a dead server would read as a typo.
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  /** The currency the form's amounts are in: the revised scheme's own, never the branch's. */
  const formCurrency = revising?.currency ?? currency

  const refresh = useCallback(async () => {
    try {
      setRows(await load())
      setLoadError(null)
    } catch {
      setLoadError(t('commission.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setForm(EMPTY_FORM)
    setRevising(null)
    setNextRole('')
    void refresh()
  })

  /** An amount field to the whole minor units the wire takes. Refuses what `bigFromWire` would. */
  const amountMinor = (text: string, blank: MessageKey): string => {
    if (text.trim() === '') throw new SchemeFormError(t(blank))
    const minor = parseMajorInput(text, formCurrency)
    // `parseMajorInput` accepts a leading minus and `bigFromWire` does not, and `validateScheme`
    // refuses a negative flat amount, lens tier amount, threshold and deduction alike. Said here
    // rather than let through as a 422 that stops the queue.
    if (minor < 0n) throw new SchemeFormError(t('commission.amountNegative'))
    if (minor.toString().length > 18) throw new SchemeFormError(t('commission.amountTooBig'))
    return minor.toString()
  }

  const rateMilli = (text: string): string => {
    try {
      return parseRatePercent(text).toString()
    } catch {
      throw new SchemeFormError(t('commission.rateRange'))
    }
  }

  const ruleWire = (row: RuleRow, index: number): Record<string, unknown> => {
    const number = index + 1
    switch (row.basis) {
      case 'flat':
        return {
          basis: 'flat',
          amountMinor: amountMinor(row.amount, 'commission.amountRequired'),
        }
      case 'retail': {
        if (row.base === '') {
          throw new SchemeFormError(t('commission.ruleBaseRequired', { number }))
        }
        return { basis: 'retail', rateMilliPercent: rateMilli(row.rate), base: row.base }
      }
      case 'lens_tier': {
        if (row.lensTiers.length === 0) {
          throw new SchemeFormError(t('commission.lensTierRequired', { number }))
        }
        const amountByTier: Record<string, string> = {}
        for (const tierRow of row.lensTiers) {
          const tier = tierRow.tier.trim()
          if (tier === '') throw new SchemeFormError(t('commission.lensTierNameRequired'))
          if (tier in amountByTier) {
            throw new SchemeFormError(t('commission.lensTierDuplicate', { tier }))
          }
          amountByTier[tier] = amountMinor(tierRow.amount, 'commission.amountRequired')
        }
        return { basis: 'lens_tier', amountByTier }
      }
      default:
        throw new SchemeFormError(t('commission.ruleBasisRequired', { number }))
    }
  }

  const definitionWire = (): Record<string, unknown> => {
    if (form.label.trim() === '') throw new SchemeFormError(t('commission.labelRequired'))
    if (form.rounding === '') throw new SchemeFormError(t('commission.roundingRequired'))
    if (form.earnsOn === '') throw new SchemeFormError(t('commission.earnsOnRequired'))
    if (form.attributionOrder.length === 0) {
      throw new SchemeFormError(t('commission.attributionRequired'))
    }
    if (new Set(form.attributionOrder).size !== form.attributionOrder.length) {
      // `validateScheme`: "attributionOrder lists a role twice". The chooser only offers roles that
      // are not already in the order, so this is the second lock rather than the first.
      throw new SchemeFormError(t('commission.attributionDuplicate'))
    }

    const rules: Record<string, unknown> = {}
    form.rules.forEach((row, index) => {
      const kind = row.lineKind.trim()
      if (kind === '') throw new SchemeFormError(t('commission.ruleKindRequired'))
      if (kind in rules) {
        // Worse than a refusal if it went through: `rules` is an object keyed by the kind, so the
        // second rule would replace the first without a word and that line kind would pay the
        // wrong rate for as long as the version stayed in force.
        throw new SchemeFormError(t('commission.ruleKindDuplicate', { kind }))
      }
      rules[kind] = ruleWire(row, index)
    })

    if (form.deductionKind === '') {
      throw new SchemeFormError(t('commission.remakeDeductionRequired'))
    }
    const remake: Record<string, unknown> = {
      reducingCauses: [...form.reducingCauses],
      deduction:
        form.deductionKind === 'fixed'
          ? {
              kind: 'fixed',
              amountMinor: amountMinor(form.deductionAmount, 'commission.remakeAmountRequired'),
            }
          : { kind: form.deductionKind },
    }

    let tier: Record<string, unknown> | undefined
    if (form.tierOn) {
      if (form.tierScope === '') throw new SchemeFormError(t('commission.tierScopeRequired'))
      if (form.tierMeasure === '') throw new SchemeFormError(t('commission.tierMeasureRequired'))
      if (form.steps.length === 0) throw new SchemeFormError(t('commission.tierStepsRequired'))
      let previous: bigint | undefined
      const steps = form.steps.map((step, index) => {
        if (step.inclusive === '') {
          throw new SchemeFormError(t('commission.tierInclusiveRequired', { number: index + 1 }))
        }
        const threshold = amountMinor(step.threshold, 'commission.tierThresholdRequired')
        const value = BigInt(threshold)
        // `COM-004`: "tier thresholds must be strictly increasing". Two steps at one figure have no
        // answer for which rate applies at it.
        if (previous !== undefined && value <= previous) {
          throw new SchemeFormError(t('commission.tierRising'))
        }
        previous = value
        return {
          thresholdMinor: threshold,
          inclusive: step.inclusive === 'at',
          rateMilliPercent: rateMilli(step.rate),
        }
      })
      tier = { scope: form.tierScope, measure: form.tierMeasure, mode: TIER_MODE, steps }
    }

    return {
      label: form.label.trim(),
      rounding: form.rounding,
      earnsOn: form.earnsOn,
      attributionOrder: [...form.attributionOrder],
      rules,
      ...(tier === undefined ? {} : { tier }),
      // Posted back exactly as it arrived, or left out when the version had none. A revision
      // replaces the whole definition, so omitting a bonus the shop pays would stop paying it.
      ...(form.keepMetricRules === undefined ? {} : { metricRules: [...form.keepMetricRules] }),
      remake,
    }
  }

  const save = useCallback(async () => {
    let definition: Record<string, unknown>
    try {
      definition = definitionWire()
    } catch (inputError) {
      setError(
        inputError instanceof SchemeFormError || inputError instanceof MoneyInputError
          ? inputError.message
          : t('commission.cannotSave'),
      )
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path:
        revising === null
          ? '/commission/schemes'
          : `/commission/schemes/${revising.schemeId}/versions`,
      label: revising === null ? t('commission.queueNewLabel') : t('commission.queueReviseLabel'),
      // No `expectedVersion`: both routes insert a new row rather than update one, and the service
      // accepts none. The idempotency key the queue supplies is what makes a double tap one write.
      body: revising === null ? { branchId, ...definition } : definition,
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, enqueue, form, formCurrency, revising, t])

  const publish = useCallback(
    async (row: CommissionSchemeVersionData) => {
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/commission/schemes/${row.schemeId}/versions/${String(row.versionNumber)}/publication`,
        label: t('commission.queuePublishLabel'),
        // `ARC-007`: the row version this screen was showing, so two people putting different
        // versions in force is a conflict rather than the second one winning.
        body: { expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  /** `COM-002`: a scheme that pays on margin, by a rule's basis or by the tier's measure. */
  const paysOnMargin = (row: CommissionSchemeVersionData): boolean =>
    row.rules.some((rule) => rule.basis === 'margin') || row.tier?.measure === 'margin'

  /** The first value in this version that the form cannot write back, or null. */
  const unwritable = (row: CommissionSchemeVersionData): string | null => {
    if (!WRITABLE.rounding.includes(row.rounding)) return row.rounding
    if (!WRITABLE.earnsOn.includes(row.earnsOn)) return row.earnsOn
    const role = row.attributionOrder.find((r) => !WRITABLE.role.includes(r))
    if (role !== undefined) return role
    for (const rule of row.rules) {
      if (!WRITABLE.basis.includes(rule.basis)) return rule.basis
      // A retail rule always carries a base, because the codec refuses one without it, so an
      // absent base is a contract gap. Reported as the basis rather than as an empty string,
      // which would put a hole in the sentence that explains the refusal.
      if (rule.basis === 'retail' && !WRITABLE.base.includes(rule.base ?? '')) {
        return rule.base === undefined || rule.base === '' ? rule.basis : rule.base
      }
    }
    if (row.tier !== undefined) {
      if (row.tier.mode !== TIER_MODE) return row.tier.mode
      if (!WRITABLE.tierScope.includes(row.tier.scope)) return row.tier.scope
      if (!WRITABLE.tierMeasure.includes(row.tier.measure)) return row.tier.measure
    }
    if (!WRITABLE.deduction.includes(row.remake.deductionKind)) return row.remake.deductionKind
    const cause = row.remake.reducingCauses.find((c) => !WRITABLE.cause.includes(c))
    return cause ?? null
  }

  const startRevision = (row: CommissionSchemeVersionData): void => {
    const cannot = unwritable(row)
    if (cannot !== null) {
      /**
       * Refused rather than warned.
       *
       * A revision replaces the whole definition, so a value this screen cannot show would be
       * absent from the next version: a margin rule becomes no rule, an unknown rounding becomes
       * whatever was chosen here. A warning on a form still lets the write through, and this
       * repository's rule is that a gate is impossible rather than discouraged.
       */
      setError(t('commission.cannotReviseUnknown', { what: cannot }))
      return
    }
    setError(null)
    setRevising({ schemeId: row.schemeId, currency: row.currency })
    setNextRole('')
    setForm({
      label: row.label,
      rounding: row.rounding,
      earnsOn: row.earnsOn,
      attributionOrder: [...row.attributionOrder],
      rules: row.rules.map((rule) => ({
        lineKind: rule.lineKind,
        basis: rule.basis,
        // Bare digits at the currency's own scale, which is what the field takes back: the grouped
        // display form comes out of `parseMajorInput` as "Not a valid amount".
        amount:
          rule.amountMinor === undefined
            ? ''
            : formatMinorForInput(parseMinorUnits(rule.amountMinor), row.currency),
        rate: rule.rateMilliPercent === undefined ? '' : formatRatePercent(rule.rateMilliPercent),
        base: rule.base ?? '',
        lensTiers: (rule.amountByTier ?? []).map((pair) => ({
          tier: pair.tier,
          amount: formatMinorForInput(parseMinorUnits(pair.amountMinor), row.currency),
        })),
      })),
      tierOn: row.tier !== undefined,
      tierScope: row.tier?.scope ?? '',
      tierMeasure: row.tier?.measure ?? '',
      steps: (row.tier?.steps ?? []).map((step) => ({
        threshold: formatMinorForInput(parseMinorUnits(step.thresholdMinor), row.currency),
        inclusive: step.inclusive ? 'at' : 'above',
        rate: formatRatePercent(step.rateMilliPercent),
      })),
      reducingCauses: [...row.remake.reducingCauses],
      deductionKind: row.remake.deductionKind,
      deductionAmount:
        row.remake.deductionAmountMinor === undefined
          ? ''
          : formatMinorForInput(parseMinorUnits(row.remake.deductionAmountMinor), row.currency),
      ...(row.metricRules === undefined ? {} : { keepMetricRules: row.metricRules }),
    })
  }

  /** What one rule pays, in the shop's words. `ARC-020`: an unknown basis prints as itself. */
  const ruleFigure = (rule: SchemeRuleData, rowCurrency: string): string => {
    switch (rule.basis) {
      case 'flat':
        return rule.amountMinor === undefined
          ? rule.basis
          : t('commission.figureFlat', {
              amount: formatWireForDisplay(rule.amountMinor, rowCurrency),
            })
      // A rate the server did not send is a contract gap, never a rate of nothing: printing
      // "0 per cent of the price" would be a figure somebody acts on, so the basis word stands
      // alone instead. Same reasoning as `ROLE-001a`'s withheld cost, applied to a missing field.
      case 'retail':
        return rule.rateMilliPercent === undefined
          ? rule.basis
          : t('commission.figureRetail', {
              rate: formatRatePercent(rule.rateMilliPercent),
              base: commissionBaseLabel(t, rule.base ?? ''),
            })
      case 'margin':
        return rule.rateMilliPercent === undefined
          ? rule.basis
          : t('commission.figureMargin', { rate: formatRatePercent(rule.rateMilliPercent) })
      case 'lens_tier':
        // A list of pairs rather than a sentence, so there is nothing to build: each pair is its
        // own whole message and the comma between them is punctuation, which is how
        // `purchasing.invoices` already prints a list of supplier invoices and their match states.
        return (rule.amountByTier ?? [])
          .map((pair) =>
            t('commission.lensTierPair', {
              tier: pair.tier,
              amount: formatWireForDisplay(pair.amountMinor, rowCurrency),
            }),
          )
          .join(', ')
      default:
        return rule.basis
    }
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.commissionScheme'))
  const unusedRoles = ATTRIBUTION_ROLES.filter((role) => !form.attributionOrder.includes(role))
  /** The version in force for each scheme, so an older draft can say what it would replace. */
  const inForce = new Map(
    (rows ?? [])
      .filter((row) => row.status === 'published')
      .map((row) => [row.schemeId, row.versionNumber]),
  )

  return (
    <section className="lm-screen" aria-labelledby="commission-heading">
      <h1 id="commission-heading" className="lm-screen__heading">
        {t('commission.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/* The form below stays rendered in every state: it is how the first pay rule gets written. */}
      <DataSurface
        state={
          loadError !== null && rows === null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('commission.what')}
        emptyMessage={t('commission.empty')}
      >
        <ul className="lm-scheme-list">
          {(rows ?? []).map((row) => {
            const margin = paysOnMargin(row)
            const replaces = inForce.get(row.schemeId)
            return (
              <li key={row.id} className="lm-scheme">
                <span className="lm-scheme__label">{row.label}</span>
                <span className="lm-scheme__version" dir="ltr">
                  <Ltr>{`v${String(row.versionNumber)}`}</Ltr>
                </span>
                <span className="lm-scheme__status">{commissionStatusWord(t, row.status)}</span>

                {row.branchId === branchId ? null : (
                  // The list spans every branch this person can reach, and a scheme's currency is
                  // its branch's, so a row in another currency needs to say where it is from.
                  <span className="lm-scheme__where">
                    {t('commission.otherBranch', {
                      branch:
                        branches.find((candidate) => candidate.id === row.branchId)?.name ??
                        // Not the id. A branch missing from this person's list is a branch they
                        // cannot reach, and a UUID is the defect this replaced.
                        t('commission.anotherBranch'),
                    })}
                  </span>
                )}

                <span className="lm-scheme__fact">
                  {t('commission.roundsBy', { mode: commissionRoundingLabel(t, row.rounding) })}
                </span>
                <span className="lm-scheme__fact">
                  {t('commission.earnedWhen', { when: commissionEarnsOnLabel(t, row.earnsOn) })}
                </span>
                <span className="lm-scheme__fact">
                  {t('commission.creditOrder', {
                    roles: row.attributionOrder
                      .map((role) => attributionRoleLabel(t, role))
                      .join(', '),
                  })}
                </span>

                {row.rules.length === 0 ? (
                  // Accepted by the server and pays nobody: `COM-001` says a kind with no rule
                  // earns nothing, and a scheme with no rules at all has no kind with one.
                  <span className="lm-scheme__fact">{t('commission.noRules')}</span>
                ) : (
                  <table className="lm-line-table">
                    <caption className="lm-rx-grid__caption">
                      {t('commission.rulesCaption', { label: row.label })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('commission.col.lineKind')}</th>
                        <th scope="col">{t('commission.col.pays')}</th>
                        <th scope="col">{t('commission.col.figure')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.rules.map((rule) => (
                        <tr key={rule.lineKind}>
                          {/* Not wrapped in `Ltr`: the kind is whatever the shop typed as the
                              sale line's category, so it can be Urdu, and pinning it left to
                              right would reverse the one string here that nothing else knows. */}
                          <th scope="row" className="lm-line-table__description">
                            {rule.lineKind}
                          </th>
                          <td>{commissionBasisLabel(t, rule.basis)}</td>
                          {/* A plain cell, not `lm-line-table__num` and no `dir`: the figure is a
                              translated sentence with an amount inside it, and that class is the
                              monospace tabular column for a bare number. `translate` already wraps
                              the amount in an isolate in a right-to-left locale (`RTL-03`), so the
                              money reads correctly without the whole sentence being turned round. */}
                          <td>{ruleFigure(rule, row.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {row.tier === undefined ? null : (
                  // Three facts rather than one sentence with three holes: each hole is filled by
                  // a word out of `status-labels.ts`, and those words have to read as choices in
                  // the form below as well, so none of them can be a mid-sentence fragment.
                  <>
                    <span className="lm-scheme__fact">
                      {t('commission.tierSteps', { count: row.tier.steps.length })}
                    </span>
                    <span className="lm-scheme__fact">
                      {t('commission.tierScopeFact', {
                        scope: commissionTierScopeLabel(t, row.tier.scope),
                      })}
                    </span>
                    <span className="lm-scheme__fact">
                      {t('commission.tierMeasureFact', {
                        measure: commissionTierMeasureLabel(t, row.tier.measure),
                      })}
                    </span>
                  </>
                )}

                {row.remake.deductionKind === 'none' || row.remake.reducingCauses.length === 0 ? (
                  <span className="lm-scheme__fact">{t('commission.remakeNoReduce')}</span>
                ) : (
                  <span className="lm-scheme__fact">
                    {t('commission.remakeReduces', {
                      causes: row.remake.reducingCauses
                        .map((cause) => remakeCauseLabel(t, cause))
                        .join(', '),
                      deduction: commissionDeductionLabel(t, row.remake.deductionKind),
                    })}
                  </span>
                )}

                {row.metricRules === undefined || row.metricRules.length === 0 ? null : (
                  // Said on the row, because a revision carries them through and somebody has to
                  // know this screen is not where they are changed.
                  <span className="lm-scheme__fact">
                    {t('commission.metricsCarried', { count: row.metricRules.length })}
                  </span>
                )}

                {row.withdrawReason === undefined ? null : (
                  <span className="lm-scheme__where">
                    {t('commission.withdrawnBecause', { reason: row.withdrawReason })}
                  </span>
                )}

                {margin ? (
                  // Publishing re-runs `validateScheme` with the publisher's own cost grant, which
                  // this screen cannot read, so neither button is offered on a margin scheme.
                  <span className="lm-scheme__fact">{t('commission.marginLocked')}</span>
                ) : (
                  <>
                    {row.status === 'draft' ? (
                      <button
                        type="button"
                        className="lm-button lm-button--primary"
                        onClick={() => void publish(row)}
                      >
                        {t('commission.publish')}
                      </button>
                    ) : null}

                    {row.status === 'draft' &&
                    replaces !== undefined &&
                    replaces > row.versionNumber ? (
                      // The server allows it and withdraws the newer version in the same
                      // transaction, so this is a sentence rather than a refusal: inventing one
                      // here would be a rule the spec does not have.
                      <span className="lm-scheme__fact">
                        {t('commission.publishReplaces', { number: replaces })}
                      </span>
                    ) : null}

                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        startRevision(row)
                      }}
                    >
                      {t('commission.revise')}
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      {rows !== null && rows.some((row) => row.status === 'draft') ? (
        // There is no withdrawal route for a scheme version, unlike a promotion, so an abandoned
        // draft stays on this list for good. Said rather than left to be wondered about.
        <p className="lm-hint">{t('commission.noWithdrawal')}</p>
      ) : null}

      <h2 className="lm-scheme-form__heading">
        {revising === null ? t('commission.newHeading') : t('commission.reviseHeading')}
      </h2>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('commission.labelLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={form.label}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, label: value }))
          }}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('commission.roundingLabel')}</span>
        <select
          className="lm-input"
          value={form.rounding}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, rounding: value }))
          }}
        >
          <option value="">{t('commission.chooseRounding')}</option>
          {ROUNDINGS.map((mode) => (
            <option key={mode} value={mode}>
              {commissionRoundingLabel(t, mode)}
            </option>
          ))}
        </select>
      </label>
      {/* Appendix D names no mode for this figure and the domain says so in as many words, so the
          scheme carries one and there is nothing to default to. */}
      <p className="lm-hint">{t('commission.roundingMeans')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('commission.earnsOnLabel')}</span>
        <select
          className="lm-input"
          value={form.earnsOn}
          onChange={(e) => {
            const value = e.currentTarget.value
            setForm((previous) => ({ ...previous, earnsOn: value }))
          }}
        >
          <option value="">{t('commission.chooseEarnsOn')}</option>
          {EARNS_ON.map((when) => (
            <option key={when} value={when}>
              {commissionEarnsOnLabel(t, when)}
            </option>
          ))}
        </select>
      </label>
      {/* `POS-007`'s layaway puts months between the sale and the last payment, which is why the
          spec giving no trigger is a decision somebody has to take. */}
      <p className="lm-hint">{t('commission.earnsOnMeans')}</p>

      <fieldset className="lm-scheme-block">
        <legend>{t('commission.attributionLegend')}</legend>
        {/* `COM-003`: the first role on the list that the sale actually recorded is credited. */}
        <p className="lm-hint">{t('commission.attributionMeans')}</p>

        {form.attributionOrder.length === 0 ? null : (
          <ol className="lm-scheme-order">
            {form.attributionOrder.map((role, index) => (
              <li key={role} className="lm-scheme-order__row">
                <span>
                  {t('commission.attributionRow', {
                    position: index + 1,
                    role: attributionRoleLabel(t, role),
                  })}
                </span>
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    setForm((previous) => ({
                      ...previous,
                      attributionOrder: previous.attributionOrder.filter((r) => r !== role),
                    }))
                  }}
                >
                  {t('commission.attributionRemove', { role: attributionRoleLabel(t, role) })}
                </button>
              </li>
            ))}
          </ol>
        )}

        {unusedRoles.length === 0 ? null : (
          <>
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('commission.attributionChoose')}</span>
              <select
                className="lm-input"
                value={nextRole}
                onChange={(e) => {
                  setNextRole(e.currentTarget.value)
                }}
              >
                <option value="">{t('commission.chooseRole')}</option>
                {unusedRoles.map((role) => (
                  <option key={role} value={role}>
                    {attributionRoleLabel(t, role)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                if (nextRole === '') return
                setForm((previous) => ({
                  ...previous,
                  attributionOrder: [...previous.attributionOrder, nextRole],
                }))
                setNextRole('')
              }}
            >
              {t('commission.attributionAdd')}
            </button>
          </>
        )}

        {/* `DEFAULT_ATTRIBUTION_ORDER` is the spec's own order, offered "so nobody retypes it" and
            still the tenant's decision: a button that fills it in rather than a pre-filled form. */}
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setForm((previous) => ({
              ...previous,
              attributionOrder: [...DEFAULT_ATTRIBUTION_ORDER],
            }))
          }}
        >
          {t('commission.attributionUseSpec')}
        </button>
      </fieldset>

      <fieldset className="lm-scheme-block">
        <legend>{t('commission.rulesLegend')}</legend>
        {/* The kind is matched against `sales_order_line.category`, which is a typed field with no
            vocabulary behind it, so a kind that matches nothing earns nothing and nothing says so. */}
        <p className="lm-hint">{t('commission.rulesMeans')}</p>
        {/* Margin is one of `COM-002`'s four bases and is the one this screen cannot write. */}
        <p className="lm-hint">{t('commission.marginNotOffered')}</p>

        {form.rules.length === 0 ? <p className="lm-hint">{t('commission.noRulesYet')}</p> : null}

        {form.rules.map((rule, index) => {
          const number = index + 1
          const change = (patch: Partial<RuleRow>): void => {
            setForm((previous) => ({
              ...previous,
              rules: previous.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
            }))
          }
          return (
            /**
             * Keyed on the position alone, deliberately.
             *
             * `condition-builder.tsx` keys its rows on the position and the chosen field, which is
             * safe there because that field is a select. Doing the same with the line kind, which
             * is typed, changes the key on every keystroke: React unmounts the input, the new one
             * mounts unfocused, and the field takes one character per click. Every value on the row
             * lives in `form` rather than in the DOM, so the position is a sufficient key.
             */
            <div className="lm-scheme-rule" key={String(index)}>
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('commission.ruleKindLabel', { number })}</span>
                <input
                  className="lm-input"
                  autoComplete="off"
                  value={rule.lineKind}
                  onChange={(e) => {
                    change({ lineKind: e.currentTarget.value })
                  }}
                />
              </label>

              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">
                  {t('commission.ruleBasisLabel', { number })}
                </span>
                <select
                  className="lm-input"
                  value={rule.basis}
                  onChange={(e) => {
                    change({ basis: e.currentTarget.value })
                  }}
                >
                  <option value="">{t('commission.chooseBasis')}</option>
                  {OFFERED_BASES.map((basis) => (
                    <option key={basis} value={basis}>
                      {commissionBasisLabel(t, basis)}
                    </option>
                  ))}
                </select>
              </label>

              {rule.basis === 'flat' ? (
                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">
                    {t('commission.ruleAmountLabel', { number, currency: formCurrency })}
                  </span>
                  <input
                    className="lm-input"
                    inputMode="decimal"
                    autoComplete="off"
                    value={rule.amount}
                    onChange={(e) => {
                      change({ amount: e.currentTarget.value })
                    }}
                  />
                </label>
              ) : null}

              {rule.basis === 'retail' ? (
                <>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('commission.ruleRateLabel', { number })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={rule.rate}
                      onChange={(e) => {
                        change({ rate: e.currentTarget.value })
                      }}
                    />
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('commission.ruleBaseLabel', { number })}
                    </span>
                    <select
                      className="lm-input"
                      value={rule.base}
                      onChange={(e) => {
                        change({ base: e.currentTarget.value })
                      }}
                    >
                      <option value="">{t('commission.chooseBase')}</option>
                      {RETAIL_BASES.map((base) => (
                        <option key={base} value={base}>
                          {commissionBaseLabel(t, base)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/* Whether a line discount reduces pay is the shop absorbing the discount or the
                      staff member sharing it, and the spec says only "percentage of retail". */}
                  <p className="lm-hint">{t('commission.ruleBaseMeans')}</p>
                </>
              ) : null}

              {rule.basis === 'lens_tier' ? (
                <>
                  {rule.lensTiers.map((tierRow, tierIndex) => (
                    // Position alone, for the reason the rule rows above give.
                    <div className="lm-scheme-rule__tier" key={String(tierIndex)}>
                      <label className="lm-field lm-field--inline">
                        <span className="lm-field__label">
                          {t('commission.lensTierNameLabel', {
                            number,
                            tierNumber: tierIndex + 1,
                          })}
                        </span>
                        <input
                          className="lm-input"
                          autoComplete="off"
                          value={tierRow.tier}
                          onChange={(e) => {
                            const value = e.currentTarget.value
                            change({
                              lensTiers: rule.lensTiers.map((row, i) =>
                                i === tierIndex ? { ...row, tier: value } : row,
                              ),
                            })
                          }}
                        />
                      </label>
                      <label className="lm-field lm-field--inline">
                        <span className="lm-field__label">
                          {t('commission.lensTierAmountLabel', {
                            number,
                            tierNumber: tierIndex + 1,
                            currency: formCurrency,
                          })}
                        </span>
                        <input
                          className="lm-input"
                          inputMode="decimal"
                          autoComplete="off"
                          value={tierRow.amount}
                          onChange={(e) => {
                            const value = e.currentTarget.value
                            change({
                              lensTiers: rule.lensTiers.map((row, i) =>
                                i === tierIndex ? { ...row, amount: value } : row,
                              ),
                            })
                          }}
                        />
                      </label>
                    </div>
                  ))}
                  {/* "Lens tier" is not defined in the spec, so the names are whatever this shop
                      calls them and they have to match what a sale line carries. */}
                  <p className="lm-hint">{t('commission.lensTierMeans')}</p>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      change({ lensTiers: [...rule.lensTiers, { tier: '', amount: '' }] })
                    }}
                  >
                    {t('commission.lensTierAdd', { number })}
                  </button>
                </>
              ) : null}

              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => {
                  setForm((previous) => ({
                    ...previous,
                    rules: previous.rules.filter((_, i) => i !== index),
                  }))
                }}
              >
                {t('commission.ruleRemove', { number })}
              </button>
            </div>
          )
        })}

        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setForm((previous) => ({ ...previous, rules: [...previous.rules, EMPTY_RULE] }))
          }}
        >
          {t('commission.ruleAdd')}
        </button>
      </fieldset>

      <fieldset className="lm-scheme-block">
        <legend>{t('commission.tierLegend')}</legend>

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('commission.tierOnLabel')}</span>
          <input
            type="checkbox"
            checked={form.tierOn}
            onChange={(e) => {
              const value = e.currentTarget.checked
              setForm((previous) => ({ ...previous, tierOn: value }))
            }}
          />
        </label>
        {/* `whole_period` is the only mode the domain defines: once the threshold is cleared the
            higher rate applies to every qualifying line in the period, not only the part above. */}
        <p className="lm-hint">{t('commission.tierMeans')}</p>

        {form.tierOn ? (
          <>
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('commission.tierScopeLabel')}</span>
              <select
                className="lm-input"
                value={form.tierScope}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setForm((previous) => ({ ...previous, tierScope: value }))
                }}
              >
                <option value="">{t('commission.chooseTierScope')}</option>
                {TIER_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {commissionTierScopeLabel(t, scope)}
                  </option>
                ))}
              </select>
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('commission.tierMeasureLabel')}</span>
              <select
                className="lm-input"
                value={form.tierMeasure}
                onChange={(e) => {
                  const value = e.currentTarget.value
                  setForm((previous) => ({ ...previous, tierMeasure: value }))
                }}
              >
                <option value="">{t('commission.chooseTierMeasure')}</option>
                {OFFERED_TIER_MEASURES.map((measure) => (
                  <option key={measure} value={measure}>
                    {commissionTierMeasureLabel(t, measure)}
                  </option>
                ))}
              </select>
            </label>
            {/* A margin measure makes the whole scheme a margin scheme to `usesMargin`, so it is
                left out for the same reason the margin basis is. */}
            <p className="lm-hint">{t('commission.marginNotOffered')}</p>

            {form.steps.map((step, index) => {
              const number = index + 1
              const change = (patch: Partial<StepRow>): void => {
                setForm((previous) => ({
                  ...previous,
                  steps: previous.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
                }))
              }
              return (
                <div className="lm-scheme-rule" key={String(index)}>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('commission.tierThresholdLabel', { number, currency: formCurrency })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={step.threshold}
                      onChange={(e) => {
                        change({ threshold: e.currentTarget.value })
                      }}
                    />
                  </label>

                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('commission.tierInclusiveLabel', { number })}
                    </span>
                    <select
                      className="lm-input"
                      value={step.inclusive}
                      onChange={(e) => {
                        change({ inclusive: e.currentTarget.value })
                      }}
                    >
                      <option value="">{t('commission.chooseInclusive')}</option>
                      <option value="at">{t('commission.inclusiveAtOrAbove')}</option>
                      <option value="above">{t('commission.inclusiveAbove')}</option>
                    </select>
                  </label>

                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('commission.tierRateLabel', { number })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={step.rate}
                      onChange={(e) => {
                        change({ rate: e.currentTarget.value })
                      }}
                    />
                  </label>

                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setForm((previous) => ({
                        ...previous,
                        steps: previous.steps.filter((_, i) => i !== index),
                      }))
                    }}
                  >
                    {t('commission.tierStepRemove', { number })}
                  </button>
                </div>
              )
            })}

            {/* A checkbox that starts unticked would be a default of false, and the spec says only
                "above", so each step is asked. */}
            <p className="lm-hint">{t('commission.tierInclusiveMeans')}</p>

            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                setForm((previous) => ({ ...previous, steps: [...previous.steps, EMPTY_STEP] }))
              }}
            >
              {t('commission.tierStepAdd')}
            </button>
          </>
        ) : null}
      </fieldset>

      <fieldset className="lm-scheme-block">
        <legend>{t('commission.remakeLegend')}</legend>
        {/* `COM-007`: a remake the shop caused is always on the statement. Whether it reduces pay
            is the tenant's to say, which is why `reducingCauses` has no default. */}
        <p className="lm-hint">{t('commission.remakeMeans')}</p>

        <fieldset className="lm-scheme-causes">
          <legend>{t('commission.remakeCausesLegend')}</legend>
          {REMAKE_CAUSES.map((cause) => (
            <label className="lm-field lm-field--inline" key={cause}>
              <span className="lm-field__label">{remakeCauseLabel(t, cause)}</span>
              <input
                type="checkbox"
                checked={form.reducingCauses.includes(cause)}
                onChange={(e) => {
                  const on = e.currentTarget.checked
                  setForm((previous) => ({
                    ...previous,
                    reducingCauses: on
                      ? [...previous.reducingCauses, cause]
                      : previous.reducingCauses.filter((c) => c !== cause),
                  }))
                }}
              />
            </label>
          ))}
        </fieldset>

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('commission.remakeDeductionLabel')}</span>
          <select
            className="lm-input"
            value={form.deductionKind}
            onChange={(e) => {
              const value = e.currentTarget.value
              setForm((previous) => ({ ...previous, deductionKind: value }))
            }}
          >
            <option value="">{t('commission.chooseDeduction')}</option>
            {DEDUCTION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {commissionDeductionLabel(t, kind)}
              </option>
            ))}
          </select>
        </label>

        {form.deductionKind === 'fixed' ? (
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">
              {t('commission.remakeAmountLabel', { currency: formCurrency })}
            </span>
            <input
              className="lm-input"
              inputMode="decimal"
              autoComplete="off"
              value={form.deductionAmount}
              onChange={(e) => {
                const value = e.currentTarget.value
                setForm((previous) => ({ ...previous, deductionAmount: value }))
              }}
            />
          </label>
        ) : null}

        {form.deductionKind !== '' &&
        form.deductionKind !== 'none' &&
        form.reducingCauses.length === 0 ? (
          // Accepted by the server and takes nothing back, ever, because the deduction only runs
          // for a cause on the list. A sentence rather than a refusal: the spec permits it.
          <p className="lm-hint">{t('commission.remakeCausesEmpty')}</p>
        ) : null}
      </fieldset>

      <button type="button" className="lm-button lm-button--primary" onClick={() => void save()}>
        {revising === null ? t('commission.saveNew') : t('commission.saveRevision')}
      </button>

      {revising === null ? null : (
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setRevising(null)
            setForm(EMPTY_FORM)
            setNextRole('')
            setError(null)
          }}
        >
          {t('commission.cancelRevision')}
        </button>
      )}
    </section>
  )
}
